import { ElementRef, Injectable, Renderer2 } from '@angular/core';
import { EquationService } from '../equation-editor/equation.service';
import { IConfirmationService } from '../confirmation/confirmation.service.interface';
import { EquationEditorComponent } from '../equation-editor/equation-editor.component';
import { ATEquation } from '../equation-editor/equation.models';

/**
 * Service to handle equation-related operations in the input component.
 * Manages equation editor state, modal visibility, and equation manipulation.
 */
@Injectable({
  providedIn: 'root',
})
export class InputEquationService {
  private _currentEquationElement: Element | null = null;

  constructor(
    private _equationService: EquationService
  ) { }

  /**
   * Handles double-click events on the input area to edit equations
   * @param event Mouse event from double-click
   * @param userMessageDiv Reference to the message input element
   */
  public getATEquation(equationElement: Element): ATEquation {
    let equation = equationElement.getAttribute('data-mathml')!;

    // Fix unescaped ampersands in element content
    // Only escape & if it's not already part of an entity
    equation = equation.replace(
      /&(?![a-zA-Z]+;|#[0-9]+;|#x[0-9a-fA-F]+;)/g,
      '&amp;'
    );

    return { equation: equation };
  }

  public startEditEquation(equationElement: Element) {
    this._currentEquationElement = equationElement;
  }

  public tryGetEquationElement(event: MouseEvent): Element | null {
    const target = event.target as HTMLElement;

    // Check if the target is an equation element
    const equationElement = target.closest(this._equationService.SABISEquationElementName);

    return equationElement;
  }

  /**
   * Handles keyboard events in the equation modal
   * @param event Keyboard event
   * @param callbacks Object containing callback functions for confirm and close actions
   */
  public handleEquationKeyDown(
    event: KeyboardEvent,
    callbacks: {
      onConfirm: () => void;
      onClose: () => void;
    }
  ): void {
    if (event.key === 'Escape') {
      callbacks.onClose();
    }
    if (event.key === 'Enter') {
      callbacks.onConfirm();
    }
  }

  /**
   * Confirms the equation modal and inserts/updates the equation
   * @param equationEditor Reference to the equation editor component
   * @param userMessageDiv Reference to the message input element
   * @param renderer Renderer2 instance for DOM manipulation
   * @param maxCountDirective Reference to the max chars directive
   * @param showWarning Whether the character limit warning is shown
   * @param confirmationService The confirmation service for showing alerts
   * @param onSuccess Callback to execute on successful confirmation
   */
  public async confirmEquation(
    equationEditor: EquationEditorComponent,
    userMessageDiv: ElementRef<HTMLTextAreaElement>,
    renderer: Renderer2,
    confirmationService: IConfirmationService
  ): Promise<{ isEmpty: boolean }> {

    // Read equation from control
    const equationData = await equationEditor.getEquation();

    // Stop if empty
    if (!equationData.hasValue) {
      this.alertEmptyEquation(confirmationService);
      return { isEmpty: true };
    }

    // Edit existing equation
    if (!!this._currentEquationElement) {
      this._equationService.replaceImage(
        renderer,
        userMessageDiv,
        this._currentEquationElement,
        equationData.equation!
      );
      this._currentEquationElement = null;
    }
    // Insert new equation
    else {
      this._equationService.insertEquationFromImageHtml(userMessageDiv, equationData.equation!);
    }

    return { isEmpty: false };
  }

  /**
   * Closes the equation modal without saving
   */
  public closeEquationModal(): void {
    this._currentEquationElement = null;
  }

  /**
   * Handles equation deletion via backspace or delete key
   * @param event Event (keyboard or input event)
   */
  public deleteEquation(event: Event): void {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      const node = range.startContainer;

      const equation = this.findClosestEquation(node);
      if (equation) {
        equation.remove();
        event.preventDefault();
      }
    }
  }

  /**
   * Finds the closest equation element in the DOM hierarchy
   * @param node Starting node to search from
   * @returns The closest equation element or null
   */
  private findClosestEquation(node: Node): HTMLElement | null {
    let current: Node | null = node;

    while (current) {
      if (
        current instanceof HTMLElement &&
        current.tagName.toLowerCase() === 'span' &&
        current.querySelector(this._equationService.SABISEquationElementName)
      ) {
        return current;
      }
      current = current.parentNode;
    }

    return null;
  }

  /**
   * Shows an alert when user tries to save an empty equation
   * @param confirmationService The confirmation service for showing alerts
   */
  public alertEmptyEquation(confirmationService: IConfirmationService): void {
    confirmationService.alertEmptyEquation().subscribe(() => { });
  }
}

